/**
 * Turning a failed request into something a preparer can act on.
 *
 * The Xero connection errors are already written out in words with a next
 * step. Everything else surfaced whatever the API or the browser happened to
 * say -- "Request failed (502)", or "Failed to fetch" -- which tells somebody
 * that it did not work and nothing about what to do, so the next move is
 * always to ask.
 *
 * Keyed on the status rather than the message, because the message is written
 * for whoever reads the logs and the status is the part that says what kind of
 * problem this is.
 */
import { ApiError } from "./api";

export interface Described {
  /** What went wrong, in a sentence. */
  message: string;
  /** What to do about it. Empty when there is genuinely nothing to suggest. */
  nextStep: string;
}

const BY_STATUS: Record<number, Described> = {
  401: {
    message: "Your session has expired.",
    nextStep: "Sign out and sign in again.",
  },
  403: {
    message: "Your role does not allow that.",
    nextStep: "An administrator can change your role under Staff.",
  },
  404: {
    message: "That is no longer there.",
    nextStep: "Somebody may have deleted it. Reload to see the current list.",
  },
  409: {
    message: "That clashed with a change somebody else made.",
    nextStep: "Reload to see the current state, then try again.",
  },
  429: {
    message: "Too many requests at once.",
    nextStep: "Wait a few seconds and try again.",
  },
  502: {
    message: "The server did not answer in time.",
    nextStep:
      "Generation keeps running even when this times out — reload before " +
      "starting another one.",
  },
  504: {
    message: "The server did not answer in time.",
    nextStep:
      "Generation keeps running even when this times out — reload before " +
      "starting another one.",
  },
};

export function describeError(error: unknown, fallback: string): Described {
  if (error instanceof ApiError) {
    const known = BY_STATUS[error.status];
    if (known) return known;
    if (error.status >= 500) {
      return {
        message: "Something failed on our side.",
        nextStep: "Try again. If it keeps happening the server logs will say why.",
      };
    }
    // A 400 from our own API carries a message written to be read, so it is
    // better than anything that could be substituted for it.
    return { message: error.message, nextStep: "" };
  }

  // fetch rejects with a TypeError when the request never left the browser.
  if (error instanceof TypeError) {
    return {
      message: "Could not reach the server.",
      nextStep: "Check your connection and try again.",
    };
  }

  if (error instanceof Error && error.message) {
    return { message: error.message, nextStep: "" };
  }
  return { message: fallback, nextStep: "" };
}
